
import { useState } from 'react';
import { Send, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { useCompanySearch } from '@/hooks/useCompanySearch';

const ClaimCompanySection = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const { companyCards, searchTerm, setSearchTerm } = useCompanySearch();
  const [isOpen, setIsOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showCompanyDropdown, setShowCompanyDropdown] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState('');
  const [formData, setFormData] = useState({
    fullName: '',
    email: user?.email || '',
    jobTitle: '',
    message: '',
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleCompanySelect = (companyName: string) => {
    setSelectedCompany(companyName);
    setSearchTerm(companyName);
    setShowCompanyDropdown(false);
  };

  const filteredCompanies = companyCards.filter(company =>
    company.company.toLowerCase().includes(searchTerm.toLowerCase())
  ).slice(0, 5);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!selectedCompany || !formData.fullName || !formData.email || !formData.jobTitle || !formData.message) {
      toast({
        title: "Missing information",
        description: "Please fill in all fields including selecting your company",
        variant: "destructive"
      });
      return;
    }
    
    try {
      setIsSubmitting(true);
      
      const { error } = await supabase
        .from('company_claim_requests' as any)
        .insert({
          company_name: selectedCompany,
          full_name: formData.fullName,
          email: formData.email,
          job_title: formData.jobTitle,
          message: formData.message,
          user_id: user?.id
        });
      
      if (error) throw error;
      
      toast({
        title: "Request submitted",
        description: "Your request to claim this company has been submitted. An administrator will review it shortly.",
      });
      
      setIsOpen(false);
      setSelectedCompany('');
      setSearchTerm('');
      setFormData({
        fullName: '',
        email: user?.email || '',
        jobTitle: '',
        message: '',
      });
    } catch (error) {
      console.error("Error submitting claim request:", error);
      toast({
        title: "Request failed",
        description: "There was a problem submitting your request. Please try again later.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="py-20 px-6 bg-gray-50">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold mb-4 text-redflags-dark">Claim your Company Profile</h2>
          <p className="text-redflags-gray text-lg max-w-2xl mx-auto">
            Is your company already listed on RedFlags? Take control of your company's reputation by claiming and managing your profile.
          </p>
        </div>
        
        <div className="bg-white rounded-lg shadow-sm p-8 border border-redflags-light/20">
          <div className="flex flex-col space-y-4">
            <h3 className="text-xl font-medium text-redflags-dark">
              Manage Your Company's Presence
            </h3>
            
            <p className="text-redflags-gray">
              {isOpen 
                ? "Please provide your details below to request management access to your company profile."
                : "Are you a company representative? Request to manage your company profile and respond to reviews professionally."}
            </p>
            
            {!isOpen ? (
              <Button 
                onClick={() => setIsOpen(true)}
                className="bg-redflags-red hover:bg-redflags-red/90 text-white w-full md:w-auto"
              >
                <Send className="w-4 h-4 mr-2" /> 
                Request Access
              </Button>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="space-y-4">
                  <div className="space-y-2">
                    <label htmlFor="companySearch" className="text-sm font-medium text-gray-700">Search for Your Company</label>
                    <div className="relative">
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                        <Input
                          id="companySearch"
                          placeholder="Start typing your company name..."
                          value={searchTerm}
                          onChange={(e) => {
                            setSearchTerm(e.target.value);
                            setShowCompanyDropdown(e.target.value.length > 0);
                          }}
                          onFocus={() => setShowCompanyDropdown(searchTerm.length > 0)}
                          className="pl-10"
                        />
                      </div>
                      
                      {showCompanyDropdown && filteredCompanies.length > 0 && (
                        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-60 overflow-auto">
                          {filteredCompanies.map((company, index) => (
                            <button
                              key={index}
                              type="button"
                              className="w-full px-4 py-3 text-left hover:bg-gray-50 border-b border-gray-100 last:border-b-0"
                              onClick={() => handleCompanySelect(company.company)}
                            >
                              <div className="font-medium text-gray-900">{company.company}</div>
                              {company.location && (
                                <div className="text-sm text-gray-500">{company.location}</div>
                              )}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                    {selectedCompany && (
                      <div className="text-sm text-green-600">
                        ✓ Selected: {selectedCompany}
                      </div>
                    )}
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <label htmlFor="fullName" className="text-sm font-medium text-gray-700">Full Name</label>
                      <Input
                        id="fullName"
                        name="fullName"
                        placeholder="Your full name"
                        value={formData.fullName}
                        onChange={handleChange}
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <label htmlFor="email" className="text-sm font-medium text-gray-700">Email</label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        placeholder="Your work email"
                        value={formData.email}
                        onChange={handleChange}
                      />
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <label htmlFor="jobTitle" className="text-sm font-medium text-gray-700">Job Title</label>
                    <Input
                      id="jobTitle"
                      name="jobTitle"
                      placeholder="Your position at the company"
                      value={formData.jobTitle}
                      onChange={handleChange}
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <label htmlFor="message" className="text-sm font-medium text-gray-700">Message to Administrators</label>
                    <Textarea
                      id="message"
                      name="message"
                      placeholder="Please explain why you should be granted management access to this company profile..."
                      value={formData.message}
                      onChange={handleChange}
                      rows={3}
                    />
                  </div>
                </div>
                
                <div className="flex justify-end space-x-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setIsOpen(false)}
                    disabled={isSubmitting}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    className="bg-redflags-red hover:bg-redflags-red/90 text-white"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? "Submitting..." : "Send Request"}
                  </Button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ClaimCompanySection;
